import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Input, Tooltip } from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  StarFilled,
  LoadingOutlined,
  WarningOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import moment from "moment";
import debounce from "lodash/debounce";
import BuildingIcon from '../../../../assets/icons/BuildingIcon.svg'
import FilterDropdown from "./FilterDropdown";
import SelectJobMiniPagination from "./SelectJobMiniPagination";
import { JOB_TYPE_OPTIONS, JOBS_PER_PAGE } from "./selectJobData";
import { GIGS_QUERY } from "../../../../components/PlaceBidModal/gigsQuery";
import { getPersonalDetailsLocationApi } from "../../../../API/API";
import { fetchVmsRequest, fetchFacilityRequest } from "../../../../redux/actions";
import { BILLING_ELIGIBLE_SOURCE } from "../../../../shared/billing";
import { ApolloWrapper } from "../../../../components/ApolloWrapper/ApolloWrapper";
import SelectJob from "../../../../assets/icons/SelectJob.svg";
import history from "../../../../routes/history";
import "./SelectJobModal.css";

const getStoredProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("userProfile") || "null") || {};
  } catch {
    return {};
  }
};

export const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

export const isJobIncomplete = (job) => {
  const isContract = job?.JobOrderTypes?.JobOrderTypeName === "Contract";
  const hasDescription = !!(job?.Description || "").trim();
  const hasExperience = !!firstOf(job?.ExperienceLevels)?.ExperienceLevelName;
  const hasSkills = (job?.JobOrders_Skills || []).length > 0 || (job?.Skills || []).length > 0;
  const hasDuration = !isContract || !!firstOf(job?.IdealContractLength)?.IdealContractLengthName;
  return !hasDescription || !hasExperience || !hasSkills || !hasDuration;
};

export const buildEditJobState = (job) => ({
  FromEdit: true,
  NotTemplate: true,
  CompleteIncompleteJob: true,
  Id: job?.JobOrderId,
  Title: job?.Title,
  FacilityName: job?.FacilityName,
  Location: job?.Location?.LocationWithCountry,
  locobj: job?.Cities,
  JobOrderTypeName: job?.JobOrderTypes?.JobOrderTypeName,
  IdealContractLengthName: firstOf(job?.IdealContractLength)?.IdealContractLengthName,
  ExperienceLevels: job?.ExperienceLevels,
  Skills: (job?.JobOrders_Skills?.length ? job.JobOrders_Skills : job?.Skills || []).map((item) => ({
    skillName: item?.Skills?.SkillName ?? item?.SkillName,
    SkillPercent: item?.SkillPercent,
    isDisable: true,
  })),
  WorkAuthorization: job?.WorkAuthorization,
  IdealTerms: job?.IdealTerms,
  openPositions: job?.Position,
  BudgetMin: job?.BudgetMin?.$numberDecimal != null
    ? Number(job.BudgetMin.$numberDecimal)
    : job?.BudgetMin != null ? Number(job.BudgetMin) : undefined,
  BudgetType: job?.BudgetTypes?.BudgetTypeName,
  workpolicy: job?.WorkplacePolicy,
  WorkPolicy: job?.WorkplacePolicy,
  Description: job?.Description,
  isTemplate: job?.isTemplate || false,
  isPrivate: job?.isPrivate || false,
});

const DEFAULT_FETCH_PARAMS = {
  page: 1,
  search: "",
  jobTypes: [],
  locations: [],
  vms: [],
  facilityIds: [],
};

const getJobKey = (job) =>
  job?.id || job?._id || job?.JobOrderId || job?.jobId || null;

const EllipsisText = ({ className, text, zIndex, children }) => {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const check = () =>
      setTruncated(
        el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
      );

    check();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip
      title={truncated ? text : ""}
      placement="topLeft"
      mouseEnterDelay={0.2}
      zIndex={zIndex}
    >
      <span ref={ref} className={className}>
        {children ?? text}
      </span>
    </Tooltip>
  );
};

const isSameJob = (left, right) => {
  const leftKey = getJobKey(left);
  const rightKey = getJobKey(right);

  return Boolean(leftKey && rightKey && String(leftKey) === String(rightKey));
};

const SelectJobModal = ({ open, onClose, onSelect, selectedJob, zIndex = 1000, bidContext, checkIncomplete = false, autoSelectJob, showVmsFacilityFilter = true }) => {
  const routeLocation = useLocation();
  const dispatch = useDispatch();
  const [getGigs, { loading: loadingGigs, data }] = useLazyQuery(
    GIGS_QUERY,
    { fetchPolicy: "no-cache", nextFetchPolicy: "no-cache" }
  );

  const profile = getStoredProfile();
  const userSource = String(
    profile?.Source || localStorage.getItem("cognitoSource") || ""
  ).trim().toLowerCase();
  const canShowVmsFacilityFilter =
    showVmsFacilityFilter &&
    String(profile?.PvtEmpName || "").trim().toLowerCase() === "sg-ptm" &&
    userSource !== BILLING_ELIGIBLE_SOURCE;

  const vmsOptionsData = useSelector((state) => state.vmsFilterReducer?.data) || [];
  const facilityOptionsData = useSelector((state) => state.VmsFacilityReducer?.data) || [];

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [jobTypeFilter, setJobTypeFilter] = useState([]);
  const [locationFilter, setLocationFilter] = useState([]);
  const [locationOpts, setLocationOpts] = useState([]);
  const [vmsFilter, setVmsFilter] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState([]);
  const [facilityIdFilter, setFacilityIdFilter] = useState([]);
  const locationTimer = useRef(null);

  const vmsOptions = useMemo(
    () => (Array.isArray(vmsOptionsData) ? vmsOptionsData : [])
      .map((item) => item?.VMSName)
      .filter(Boolean),
    [vmsOptionsData]
  );

  const facilityNameToId = useMemo(() => {
    const map = {};
    (Array.isArray(facilityOptionsData) ? facilityOptionsData : []).forEach((item) => {
      const name = item?.FacilityName;
      if (name) map[name] = item?._id;
    });
    return map;
  }, [facilityOptionsData]);

  const facilityOptions = useMemo(() => Object.keys(facilityNameToId), [facilityNameToId]);

  useEffect(() => {
    if (canShowVmsFacilityFilter) {
      dispatch(fetchVmsRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowVmsFacilityFilter]);

  const onLocationSearch = useCallback((value) => {
    if (locationTimer.current) clearTimeout(locationTimer.current);
    if (!value) { setLocationOpts([]); return; }
    locationTimer.current = setTimeout(async () => {
      try {
        const res = await getPersonalDetailsLocationApi(value);
        const raw = res?.data?.data ?? res?.data ?? [];
        setLocationOpts(
          Array.isArray(raw)
            ? [...new Set(
              raw
                .map((loc) =>
                  loc.Location ||
                  loc.LocationWithCountry ||
                  loc.LocationLong ||
                  loc.location ||
                  loc.name ||
                  String(loc)
                )
                .filter(Boolean)
            )]
            : []
        );
      } catch {
        setLocationOpts([]);
      }
    }, 500);
  }, []);

  const fetchJobs = useCallback(async ({
    page: pageNo = DEFAULT_FETCH_PARAMS.page,
    search: searchText = DEFAULT_FETCH_PARAMS.search,
    jobTypes = DEFAULT_FETCH_PARAMS.jobTypes,
    locations = DEFAULT_FETCH_PARAMS.locations,
    vms = DEFAULT_FETCH_PARAMS.vms,
    facilityIds = DEFAULT_FETCH_PARAMS.facilityIds,
  } = DEFAULT_FETCH_PARAMS) => {
    try {
      const userProfile = getStoredProfile();
      await getGigs({
        variables: {
          MygigsProfileId: userProfile?.MasterAccountId || userProfile?.AccountId,
          all: searchText || "",
          _page: pageNo,
          _perPage: JOBS_PER_PAGE,
          _sort: "MOST_RECENT",
          JobOrderStatusName: "Published",
          JobOrderTypeName: Array.isArray(jobTypes) && jobTypes.length > 0 ? jobTypes : undefined,
          cities: Array.isArray(locations) && locations.length > 0 ? locations : undefined,
          vms: Array.isArray(vms) && vms.length > 0 ? vms : undefined,
          facilityIds: Array.isArray(facilityIds) && facilityIds.length > 0 ? facilityIds : undefined,
        },
      });
    } catch (error) {
      console.warn("Unable to fetch jobs", error);
    }
  }, [getGigs]);

  useEffect(() => {
    setPage(1);
    const initialSearch = autoSelectJob?.jobTitle || "";
    if (initialSearch) setSearch(initialSearch);
    fetchJobs({ page: 1, search: initialSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchJobs]);

  useEffect(() => {
    if (open) {
      setSelected(selectedJob || null);
    }
  }, [open, selectedJob]);

  const autoSelectedRef = useRef(false);

  const debouncedSearch = useRef(
    debounce((text, filters) => {
      fetchJobs({ search: text, ...filters });
    }, 500)
  ).current;

  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearch(text);
    setPage(1);
    debouncedSearch(text, {
      jobTypes: jobTypeFilter,
      locations: locationFilter,
      vms: vmsFilter,
      facilityIds: facilityIdFilter,
    });
  };

  const handleJobTypeChange = (val) => {
    const jobTypes = Array.isArray(val) ? val : [];
    setJobTypeFilter(jobTypes);
    setPage(1);
    fetchJobs({ search, jobTypes, locations: locationFilter, vms: vmsFilter, facilityIds: facilityIdFilter });
  };

  const handleVmsChange = (val) => {
    const nextVms = Array.isArray(val) ? val : [];
    setVmsFilter(nextVms);
    setFacilityFilter([]);
    setFacilityIdFilter([]);
    setPage(1);
    if (nextVms.length) {
      dispatch(fetchFacilityRequest({ vms: nextVms }));
    }
    fetchJobs({ search, jobTypes: jobTypeFilter, locations: locationFilter, vms: nextVms, facilityIds: [] });
  };

  const handleFacilityChange = (val) => {
    const names = Array.isArray(val) ? val : [];
    const ids = names.map((name) => facilityNameToId[name]).filter(Boolean);
    setFacilityFilter(names);
    setFacilityIdFilter(ids);
    setPage(1);
    fetchJobs({ search, jobTypes: jobTypeFilter, locations: locationFilter, vms: vmsFilter, facilityIds: ids });
  };

  const mappedJobs = useMemo(() => {
    const gigs = data?.gigs?.data;
    if (!Array.isArray(gigs)) return [];

    return gigs
      .filter(
        (j) =>
          j.JobOrderStatuses?.JobOrderStatusName !== "Deleted" &&
          j.JobOrderStatuses?.JobOrderStatusName !== "Expired" &&
          j.JobOrderStatuses?.JobOrderStatusName !== "Closed"
      )
      .map((job) => ({
        id: job._id,
        jobId: job.JobOrderId || job._id,
        JobOrderId: job.JobOrderId,
        JobOrderTypes: job.JobOrderTypes,
        title: job.Title || "",
        vms: job.VMS || "",
        hospital: job.FacilityName || job.CompanyName || "",
        location:
          job.Location?.LocationWithCountry ||
          [job.Cities?.CityName, job.Regions?.RegionName]
            .filter(Boolean)
            .join(", ") ||
          "",
        country: job.Location?.CountryName || "",
        rating:
          typeof job.facilityRating?.rating === "number"
            ? (job.facilityRating.rating === 0 ? null : job.facilityRating.rating.toFixed(1))
            : job.facilityRating?.rating || "4.8",
        reviews: Number.isFinite(Number(job.facilityRating?.reviews))
          ? String(Number(job.facilityRating.reviews)).padStart(2, "0")
          : "05",
        postedAgo: (() => { const m = moment(job.Created); return m.isValid() ? m.fromNow() : ""; })(),
        type: job.JobOrderTypes?.JobOrderTypeName || "",
        Description: job.Description || "",
        Shift: job.Shift || "",
        BudgetMin: job.BudgetMin ? `${job.BudgetMin}/${job.BudgetTypes?.BudgetTypeCode}` : "",
        Duration: job.Duration || "",
        ExperienceLevels: job.ExperienceLevels || [],
        startDate: (() => { const m = moment(job.Created); return m.isValid() ? m.format("D MMM YYYY") : ""; })(),
        skillsList: (job.JobOrders_Skills || [])
          .map((s) => s.Skills?.SkillName)
          .filter(Boolean),
        skills: (job.JobOrders_Skills || [])
          .map((s) => s.Skills?.SkillName)
          .filter(Boolean)
          .join(", "),
        incomplete: checkIncomplete && isJobIncomplete(job),
        raw: job,
      }));
  }, [data, checkIncomplete]);

  useEffect(() => {
    if (autoSelectedRef.current || !autoSelectJob?.jobId || selectedJob) return;
    const targetId = String(autoSelectJob.jobId);
    const match = mappedJobs.find(
      (j) => String(j.jobId) === targetId || String(j.id) === targetId
    );
    if (match) {
      autoSelectedRef.current = true;
      onSelect(match);
    }
  }, [mappedJobs, autoSelectJob, onSelect, selectedJob]);

  const filteredJobs = useMemo(() => {
    let list = mappedJobs;

    if (locationFilter?.length > 0) {
      list = list.filter((j) => locationFilter.includes(j.location));
    }

    return list;
  }, [mappedJobs, locationFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      (data?.gigs?.aggregate?.totalCount || filteredJobs.length || 0) /
      JOBS_PER_PAGE
    )
  );

  const handleConfirm = () => {
    if (!selected) return;
    onSelect(selected);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const reset = () => {
    setSelected(null);
    setSearch("");
    setPage(1);
    setJobTypeFilter([]);
    setLocationFilter([]);
    setLocationOpts([]);
    setVmsFilter([]);
    setFacilityFilter([]);
    setFacilityIdFilter([]);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      className="sjm-modal"
      width="min(984px, 95vw)"
      centered
      zIndex={zIndex}
      destroyOnClose
    >
      {/* HEADER */}
      <div className="sjm-header">
        <span className="sjm-title">Select Job</span>

        <button className="sjm-close-btn" type="button" onClick={handleClose}>
          <CloseOutlined />
        </button>
      </div>

      {/* TOOLBAR */}
      <div className="sjm-toolbar">
        <Input
          prefix={<SearchOutlined className="sjm-search-icon" />}
          placeholder="Search jobs..."
          value={search}
          onChange={handleSearchChange}
          className="sjm-search"
          allowClear
          onClear={() => {
            setSearch("");
            setPage(1);
            fetchJobs({
              search: "",
              jobTypes: jobTypeFilter,
              locations: locationFilter,
              vms: vmsFilter,
              facilityIds: facilityIdFilter,
            });
          }}
        />

        <div className="sjm-filters">
          <span className="sjm-filter-label">Filter by:</span>

          {canShowVmsFacilityFilter && (
            <>
              <FilterDropdown
                label="VMS"
                options={vmsOptions}
                value={vmsFilter}
                onChange={handleVmsChange}
                multiSelect
                searchable
              />

              <FilterDropdown
                label="Facility"
                options={facilityOptions}
                value={facilityFilter}
                onChange={handleFacilityChange}
                multiSelect
                searchable
                onSearch={(value) => dispatch(fetchFacilityRequest({ search: value, vms: vmsFilter }))}
              />
            </>
          )}

          <FilterDropdown
            label="Job type"
            options={JOB_TYPE_OPTIONS}
            value={jobTypeFilter}
            onChange={handleJobTypeChange}
            multiSelect
          />

          <FilterDropdown
            label="Location"
            options={locationOpts}
            value={locationFilter}
            onChange={(val) => {
              const locations = Array.isArray(val) ? val : [];
              setLocationFilter(locations);
              setPage(1);
              fetchJobs({
                search,
                jobTypes: jobTypeFilter,
                locations,
                vms: vmsFilter,
                facilityIds: facilityIdFilter,
              });
            }}
            multiSelect
            searchable
            onSearch={onLocationSearch}
          />

          {(
            (Array.isArray(jobTypeFilter) ? jobTypeFilter.length > 0 : false) ||
            (Array.isArray(locationFilter) ? locationFilter.length > 0 : false) ||
            (Array.isArray(vmsFilter) ? vmsFilter.length > 0 : false) ||
            (Array.isArray(facilityFilter) ? facilityFilter.length > 0 : false)
          ) && (
            <button
              type="button"
              className="sjm-filter-reset"
              onClick={() => {
                setJobTypeFilter([]);
                setLocationFilter([]);
                setLocationOpts([]);
                setVmsFilter([]);
                setFacilityFilter([]);
                setFacilityIdFilter([]);
                setSearch("");
                setPage(1);
                fetchJobs({});
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {selected?.incomplete && (
        <div className="sjm-incomplete-banner">
          <WarningOutlined className="sjm-incomplete-icon" />
          This job is missing some required details. Please complete the job before placing a bid.
        </div>
      )}

      <div className="sjm-grid">
        {loadingGigs ? (
          <div className="sjm-empty">
            <LoadingOutlined style={{ fontSize: 24, marginBottom: 8 }} />
            <span>Loading jobs...</span>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="sjm-empty">No jobs found.</div>
        ) : (
          filteredJobs.map((job) => {
            const isSelected = isSameJob(selected, job);

            return (
              <div
                key={job.id}
                className={`sjm-card${isSelected ? " sjm-card-selected" : ""}`}
                onClick={() => setSelected(isSelected ? null : job)}
              >
                <div className="sjm-card-top">
                  <div className="sjm-card-icon">
                   <img src={BuildingIcon} alt="Building" />
                  </div>

                  <div className="sjm-card-info">
                    <EllipsisText
                      className="sjm-card-title"
                      text={job.title}
                      zIndex={zIndex + 10}
                    />

                    <div className="sjm-card-hospital">
                      <EllipsisText text={job.hospital} zIndex={zIndex + 10} />

                      {job.rating != null && (
                        <span className="sjm-card-rating">
                          <StarFilled className="sjm-star" />
                          {job.rating}
                          <span className="sjm-reviews">
                            ({job.reviews} reviews)
                          </span>
                        </span>
                      )}
                    </div>

                    {job.vms ? (
                      <span className="sjm-card-vms-tag">{job.vms}</span>
                    ) : null}
                  </div>

                  <div
                    className={`sjm-radio-circle${isSelected ? " sjm-radio-checked" : ""}`}
                  >
                    {isSelected && <div className="sjm-radio-dot" />}
                  </div>
                </div>

                <div className="sjm-card-bottom">
                  <span className="sjm-card-location">
                    <EnvironmentOutlined />
                    {job.location}
                  </span>

                  <span className="sjm-card-ago">{job.postedAgo}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="sjm-footer">
        <SelectJobMiniPagination
          current={page}
          total={totalPages}
          onChange={(p) => {
            setPage(p);
            fetchJobs({ page: p, search, jobTypes: jobTypeFilter, locations: locationFilter, vms: vmsFilter, facilityIds: facilityIdFilter });
          }}
        />

        {selected?.incomplete ? (
          <button
            className="sjm-confirm-btn"
            type="button"
            onClick={() => {
              const state = {
                ...buildEditJobState(selected.raw),
                returnTo: routeLocation.pathname + routeLocation.search,
                reopenBidContext: { ...bidContext, preselectedJob: selected },
              };
              reset();
              onClose();
              history.push({ pathname: "/create-job", state });
            }}
          >
            Complete Job Details
            <ArrowRightOutlined style={{ marginLeft: 8 }} />
          </button>
        ) : (
          <button
            className={`sjm-confirm-btn${!selected ? " sjm-confirm-disabled" : ""}`}
            type="button"
            onClick={handleConfirm}
            disabled={!selected}
          >
            <span className="sjm-confirm-icon">
              <img src={SelectJob} alt="Select Job" />
            </span>
            Select Job
          </button>
        )}
      </div>
    </Modal>
  );
};
const SelectJobModalWithApollo = (props) => (
  <ApolloWrapper>
    <SelectJobModal {...props} />
  </ApolloWrapper>
);

export default SelectJobModalWithApollo;
